import PropTypes from 'prop-types';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Tooltip, Typography, CircularProgress } from '@mui/material';
import {
  getEventDescription,
  getEventLabel,
  getPatternOutcome,
  getPatternTone,
  isPatternActive,
} from './eventPatternUtils';

const sectionSx = {
  mb: 2.5,
  px: 1.75,
  py: 1.25,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const CompactStat = ({ label, value, color, bar, icon }) => (
  <Box sx={{ minWidth: 72 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.2, whiteSpace: 'nowrap', display: 'block' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.15 }}>
      {icon}
      <Typography sx={{ color, fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
    {bar != null && (
      <Box sx={{ mt: 0.4, width: 72, height: 3, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(100, Math.max(0, bar))}%`, height: '100%', bgcolor: color, borderRadius: 1 }} />
      </Box>
    )}
  </Box>
);

CompactStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
  bar: PropTypes.number,
  icon: PropTypes.node,
};

const EventPerformanceTable = ({ events, loading, signal, t }) => {
  const validEvents = (events || []).filter((event) => event?.is_valid);

  if (loading) {
    return (
      <Box sx={{ ...sectionSx, display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (validEvents.length === 0) {
    return (
      <Box sx={sectionSx}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3 }}>
          {t('sentiment.behavior.events.title', 'What history says about today')}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.75, lineHeight: 1.45 }}>
          {t('sentiment.behavior.events.empty_active', 'No strong historical pattern is active today. The market is in a neutral phase without extreme panic or euphoria signals.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={sectionSx}>
      {validEvents.map((event, index) => {
        const winRate = (event.win_rate ?? 0) * 100;
        const median = (event.median_return ?? 0) * 100;
        const isPositive = median > 0;
        const horizon = event.horizon_days ?? 20;
        const isActive = isPatternActive(event.event_name, signal);
        const patternLabel = getEventLabel(event.event_name, t);
        const patternDesc = getEventDescription(event.event_name, t);
        const tone = getPatternTone(event.event_name);
        const outcome = getPatternOutcome(event, t);
        const winColor = winRate >= 50 ? '#2e7d32' : '#d32f2f';
        const returnColor = isPositive ? '#2e7d32' : '#d32f2f';
        const ReturnIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;

        return (
          <Box
            key={`${event.event_name}-${event.horizon_days}`}
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'flex-start' },
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              mt: index > 0 ? 1.25 : 0,
              pt: index > 0 ? 1.25 : 0,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
              {index === 0 && (
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3, mb: 0.35 }}>
                  {t('sentiment.behavior.events.title', 'What history says about today')}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, flexWrap: 'wrap' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tone.accent, flexShrink: 0 }} />
                <Tooltip title={patternDesc} arrow placement="top">
                  <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: 'text.primary', lineHeight: 1.3, cursor: 'help' }}>
                    {patternLabel}
                  </Typography>
                </Tooltip>
                {!isActive && (
                  <Tooltip
                    title={t('sentiment.behavior.events.reference_note', 'Reference — today has not reached this threshold yet')}
                    arrow
                    placement="top"
                  >
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1.2, cursor: 'help' }}>
                      {t('sentiment.behavior.events.reference_short', 'Reference')}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 2, sm: 2.5 },
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                flex: '1 1 280px',
              }}
            >
              <CompactStat
                label={t('sentiment.behavior.events.samples', 'Occurrences')}
                value={event.count}
                color="#0f172a"
              />
              <CompactStat
                label={t('sentiment.behavior.events.win_rate_after', {
                  days: horizon,
                  defaultValue: `Up after ${horizon}d`,
                })}
                value={`${winRate.toFixed(0)}%`}
                color={winColor}
                bar={winRate}
              />
              <CompactStat
                label={t('sentiment.behavior.events.typical_return', 'Typical return')}
                value={`${median >= 0 ? '+' : ''}${median.toFixed(2)}%`}
                color={returnColor}
                icon={<ReturnIcon sx={{ fontSize: 15, color: returnColor }} />}
              />
              <Box
                sx={{
                  alignSelf: 'center',
                  px: 0.85,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: outcome.bg,
                }}
              >
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: outcome.color, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  {outcome.label}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

EventPerformanceTable.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    event_name: PropTypes.string.isRequired,
    horizon_days: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    win_rate: PropTypes.number,
    median_return: PropTypes.number,
    is_valid: PropTypes.bool,
  })),
  loading: PropTypes.bool,
  signal: PropTypes.shape({
    panic_score: PropTypes.number,
    fomo_score: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};

EventPerformanceTable.defaultProps = {
  events: [],
  loading: false,
};

export default EventPerformanceTable;
